import { type ChangeEvent, type DragEvent, type FC, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import {
  SearchInput,
  FilterBar,
  StatusBadge,
  PriorityBadge,
  Drawer,
  EmptyState,
  SortSelect,
  useToast,
} from '@/components/base';
import { useLegalProducts, type LegalProduct } from '@/hooks/useLegalProducts';
import { type CreateLeadInput, type Lead, useLeads } from '@/hooks/useLeads';
import {
  LeadConversionRequestError,
  useLeadConversion,
} from '@/hooks/useLeadConversion';

import {
  formatClientDuplicateSummary,
} from '@/lib/clientDuplicateDecision';
import { compareDate, compareText, stableSort } from '@/lib/listSorting';

type ViewMode = 'table' | 'kanban';

type LeadFormState = {
  name: string;
  phone: string;
  email: string;
  source: string;
  legalProductId: string;
  caseDescription: string;
  status: string;
  priority: string;
  responsibleUser: string;
  estimatedValue: string;
  nextContactAt: string;
  lastContactAt: string;
  notes: string;
};

type LeadHistoryEntry = {
  date: string;
  action: string;
  user: string;
};

type ImportStep = 'upload' | 'preview' | 'result';

type ParsedLeadImportRow = {
  lineNumber: number;
  values: Record<string, string>;
  errors: string[];
};

type ImportSummary = {
  totalRead: number;
  imported: number;
  failed: number;
  ignored: number;
};

const leadSourceOptions = [
  'WhatsApp',
  'Instagram',
  'Indicação',
  'Google Ads',
  'Site Institucional',
  'Landing Page',
  'Recorte OAB',
  'Cliente Antigo',
];

const leadStatusOptions = [
  { value: 'new', label: 'Novo' },
  { value: 'contacted', label: 'Triagem' },
  { value: 'proposal_sent', label: 'Proposta enviada' },
  { value: 'hired', label: 'Caso ativo' },
  { value: 'lost', label: 'Perdido' },
  { value: 'archived', label: 'Arquivado' },
];

const kanbanLeadStatusOptions = leadStatusOptions.filter((option) => option.value !== 'archived');

const leadPriorityOptions = [
  { value: 'low', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const leadImportColumns = [
  'nome',
  'telefone',
  'email',
  'origem',
  'produto_juridico',
  'status',
  'prioridade',
  'responsavel',
  'valor_estimado',
  'proximo_contato',
  'ultimo_contato',
  'descricao_caso',
  'observacoes',
] as const;

const leadImportHeaderLabels: Record<(typeof leadImportColumns)[number], string> = {
  nome: 'Nome',
  telefone: 'Telefone',
  email: 'E-mail',
  origem: 'Origem',
  produto_juridico: 'Produto jurídico',
  status: 'Status',
  prioridade: 'Prioridade',
  responsavel: 'Responsável',
  valor_estimado: 'Valor estimado',
  proximo_contato: 'Próximo contato',
  ultimo_contato: 'Último contato',
  descricao_caso: 'Descrição do caso',
  observacoes: 'Observações',
};

const leadImportColumnAliases: Record<string, (typeof leadImportColumns)[number]> = {
  nome: 'nome',
  name: 'nome',
  telefone: 'telefone',
  phone: 'telefone',
  fone: 'telefone',
  celular: 'telefone',
  email: 'email',
  e_mail: 'email',
  origem: 'origem',
  source: 'origem',
  produto_juridico: 'produto_juridico',
  produtojuridico: 'produto_juridico',
  legal_product_id: 'produto_juridico',
  legalproductid: 'produto_juridico',
  legal_product_name: 'produto_juridico',
  legalproductname: 'produto_juridico',
  status: 'status',
  prioridade: 'prioridade',
  priority: 'prioridade',
  responsavel: 'responsavel',
  responsible: 'responsavel',
  valor_estimado: 'valor_estimado',
  estimated_value: 'valor_estimado',
  proximo_contato: 'proximo_contato',
  next_contact_at: 'proximo_contato',
  ultimo_contato: 'ultimo_contato',
  last_contact_at: 'ultimo_contato',
  descricao_caso: 'descricao_caso',
  case_description: 'descricao_caso',
  observacoes: 'observacoes',
  notes: 'observacoes',
};

const statusLabelMap = Object.fromEntries(leadStatusOptions.map((option) => [option.value, option.label]));
const priorityLabelMap = Object.fromEntries(
  leadPriorityOptions.map((option) => [option.value, option.label])
);

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : currencyFormatter.format(value);

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = Date.parse(value);
  if (Number.isFinite(parsed)) {
    return new Date(parsed).toLocaleDateString('pt-BR');
  }

  return value;
};

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const getStatusLabel = (status: string) => statusLabelMap[status] ?? status;

const getPriorityLabel = (priority: string) => priorityLabelMap[priority] ?? priority;

const getSourceLabel = (source: string | null) => source ?? '—';

const getProductLabel = (lead: Lead) => lead.legalProductName ?? '—';

const buildLeadPayloadFromLead = (lead: Lead, statusOverride?: string): CreateLeadInput => ({
  name: lead.name,
  phone: lead.phone,
  email: lead.email,
  source: lead.source,
  legalProductId: lead.legalProductId,
  caseDescription: lead.caseDescription,
  status: statusOverride ?? lead.status,
  priority: lead.priority,
  responsibleUser: lead.responsibleUser,
  estimatedValue: lead.estimatedValue,
  nextContactAt: lead.nextContactAt,
  lastContactAt: lead.lastContactAt,
  convertedClientId: lead.convertedClientId,
  convertedCaseId: lead.convertedCaseId,
  notes: lead.notes,
});

const emptyLeadForm = (legalProductId = ''): LeadFormState => ({
  name: '',
  phone: '',
  email: '',
  source: 'WhatsApp',
  legalProductId,
  caseDescription: '',
  status: 'new',
  priority: 'normal',
  responsibleUser: 'Vinícius Borges Meschick',
  estimatedValue: '',
  nextContactAt: '',
  lastContactAt: '',
  notes: '',
});

const leadToFormState = (lead: Lead, legalProductIdFallback = ''): LeadFormState => ({
  name: lead.name,
  phone: lead.phone ?? '',
  email: lead.email ?? '',
  source: lead.source ?? 'WhatsApp',
  legalProductId: lead.legalProductId ?? legalProductIdFallback,
  caseDescription: lead.caseDescription ?? '',
  status: lead.status || 'new',
  priority: lead.priority || 'normal',
  responsibleUser: lead.responsibleUser ?? '',
  estimatedValue:
    lead.estimatedValue === null || lead.estimatedValue === undefined ? '' : String(lead.estimatedValue),
  nextContactAt: lead.nextContactAt ?? '',
  lastContactAt: lead.lastContactAt ?? '',
  notes: lead.notes ?? '',
});

const buildCreateLeadPayload = (form: LeadFormState): CreateLeadInput => ({
  name: form.name.trim(),
  phone: toNullableText(form.phone),
  email: toNullableText(form.email),
  source: toNullableText(form.source),
  legalProductId: toNullableText(form.legalProductId),
  caseDescription: toNullableText(form.caseDescription),
  status: form.status,
  priority: form.priority,
  responsibleUser: toNullableText(form.responsibleUser),
  estimatedValue: toNullableNumber(form.estimatedValue),
  nextContactAt: toNullableText(form.nextContactAt),
  lastContactAt: toNullableText(form.lastContactAt),
  notes: toNullableText(form.notes),
});

const getSelectedLeadHistory = (lead: Lead): LeadHistoryEntry[] => {
  const history: LeadHistoryEntry[] = [
    {
      date: formatDate(lead.createdAt),
      action: 'Lead cadastrado',
      user: lead.responsibleUser ?? 'Sistema',
    },
  ];

  if (lead.lastContactAt && lead.lastContactAt !== lead.createdAt) {
    history.push({
      date: formatDate(lead.lastContactAt),
      action: 'Último contato registrado',
      user: lead.responsibleUser ?? 'Sistema',
    });
  }

  return history;
};

const normalizeImportKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const leadImportStatusAliases: Record<string, string> = {
  new: 'new',
  novo: 'new',
  contacted: 'contacted',
  triagem: 'contacted',
  aguardando_documentos: 'contacted',
  analise_paga: 'contacted',
  proposal_sent: 'proposal_sent',
  proposta_enviada: 'proposal_sent',
  contrato_enviado: 'proposal_sent',
  hired: 'hired',
  caso_ativo: 'hired',
  pago: 'hired',
  lost: 'lost',
  perdido: 'lost',
  archived: 'archived',
  arquivado: 'archived',
};

const leadImportPriorityAliases: Record<string, string> = {
  low: 'low',
  baixa: 'low',
  normal: 'normal',
  medium: 'normal',
  media: 'normal',
  média: 'normal',
  high: 'high',
  alta: 'high',
  urgent: 'urgent',
  urgente: 'urgent',
};

const normalizeLeadStatus = (value: string) => {
  const normalized = normalizeImportKey(value);
  return leadImportStatusAliases[normalized] ?? 'new';
};

const normalizeLeadPriority = (value: string) => {
  const normalized = normalizeImportKey(value);
  return leadImportPriorityAliases[normalized] ?? 'normal';
};

const toImportText = (value: unknown) => {
  const text = String(value ?? '').trim();
  return text === '' ? '' : text;
};

const toImportNumber = (value: unknown) => {
  const text = toImportText(value);
  if (!text) {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const normalizeCsvDelimiter = (content: string) => {
  const firstLine = content
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .find((line) => line.trim().length > 0);

  if (!firstLine) {
    return ';';
  }

  const semicolons = (firstLine.match(/;/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;

  return semicolons >= commas ? ';' : ',';
};

const parseCsvContent = (content: string) => {
  const delimiter = normalizeCsvDelimiter(content);
  const normalized = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let currentCell = '';
  let currentRow: string[] = [];
  let inQuotes = false;

  const pushCell = () => {
    currentRow.push(currentCell);
    currentCell = '';
  };

  const pushRow = () => {
    if (currentRow.some((cell) => cell.trim() !== '')) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    const nextChar = normalized[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      pushCell();
      continue;
    }

    if (!inQuotes && char === '\n') {
      pushCell();
      pushRow();
      continue;
    }

    currentCell += char;
  }

  pushCell();
  pushRow();
  return rows;
};

const buildCsvHeaderMap = (headers: string[]) => {
  const headerMap = new Map<string, number>();

  headers.forEach((header, index) => {
    const normalized = normalizeImportKey(header);
    const mapped = leadImportColumnAliases[normalized];

    if (mapped !== undefined && !headerMap.has(mapped)) {
      headerMap.set(mapped, index);
    }
  });

  return headerMap;
};

const buildLeadImportRows = (content: string) => {
  const rows = parseCsvContent(content);

  if (rows.length === 0) {
    return { headers: [] as string[], rows: [] as ParsedLeadImportRow[] };
  }

  const [headers, ...dataRows] = rows;
  const headerMap = buildCsvHeaderMap(headers);

  const parsedRows = dataRows.map((row, index) => {
    const values = leadImportColumns.reduce<Record<string, string>>((acc, column) => {
      const headerIndex = headerMap.get(column);
      acc[column] = headerIndex === undefined ? '' : toImportText(row[headerIndex]);
      return acc;
    }, {});

    const errors: string[] = [];

    if (!values.nome) {
      errors.push('Nome obrigatório.');
    }

    const hasAnyValue = Object.values(values).some((value) => value.trim() !== '');
    if (!hasAnyValue) {
      errors.push('Linha vazia ignorada.');
    }

    return {
      lineNumber: index + 2,
      values,
      errors,
    };
  });

  return { headers, rows: parsedRows };
};

const buildLeadImportTemplateContent = () =>
  `\ufeff${leadImportColumns.map((column) => leadImportHeaderLabels[column]).join(';')}\r\n`;

const downloadTextFile = (filename: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const resolveLeadProductId = (value: string, legalProducts: LegalProduct[]) => {
  const text = toImportText(value);
  if (!text) {
    return null;
  }

  const exactProduct = legalProducts.find((product) => product.id === text);
  if (exactProduct) {
    return exactProduct.id;
  }

  const normalizedValue = normalizeImportKey(text);
  const matchedProduct = legalProducts.find((product) => normalizeImportKey(product.name) === normalizedValue);
  return matchedProduct?.id ?? null;
};

const buildLeadImportPayload = (row: ParsedLeadImportRow, legalProducts: LegalProduct[]): CreateLeadInput | null => {
  const name = row.values.nome.trim();
  if (!name) {
    return null;
  }

  return {
    name,
    phone: toNullableText(row.values.telefone),
    email: toNullableText(row.values.email),
    source: toNullableText(row.values.origem),
    legalProductId: resolveLeadProductId(row.values.produto_juridico, legalProducts),
    caseDescription: toNullableText(row.values.descricao_caso),
    status: normalizeLeadStatus(row.values.status),
    priority: normalizeLeadPriority(row.values.prioridade),
    responsibleUser: toNullableText(row.values.responsavel),
    estimatedValue: toImportNumber(row.values.valor_estimado),
    nextContactAt: toNullableText(row.values.proximo_contato),
    lastContactAt: toNullableText(row.values.ultimo_contato),
    notes: toNullableText(row.values.observacoes),
  };
};

const LeadsPage: FC = () => {
  const { addToast } = useToast();
  const { leads, isLoadingLeads, leadsError, loadLeads, createLead, updateLead, deleteLead } = useLeads({ autoLoad: false });
  const { legalProducts, loadLegalProducts } = useLegalProducts({ autoLoad: false });

  const {
    conversionLeadId,
    conversionAction,
    convertLeadToClient,
    createCaseFromLead,
  } = useLeadConversion();

  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [legalProductFilter, setLegalProductFilter] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [isSavingLead, setIsSavingLead] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [deletingLeadId, setDeletingLeadId] = useState('');
  const [draggingLeadId, setDraggingLeadId] = useState('');
  const [dragOverStatus, setDragOverStatus] = useState('');
  const [newLeadForm, setNewLeadForm] = useState<LeadFormState>(emptyLeadForm());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState<ImportStep>('upload');
  const [importFileName, setImportFileName] = useState('');
  const [importRows, setImportRows] = useState<ParsedLeadImportRow[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [isImportingLeads, setIsImportingLeads] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void loadLegalProducts();
  }, [loadLegalProducts]);

  useEffect(() => {
    void loadLeads({
      status: statusFilter || undefined,
      legalProductId: legalProductFilter || undefined,
    });
  }, [legalProductFilter, loadLeads, statusFilter]);

  const legalProductFilterOptions = useMemo(
    () => legalProducts.map((product) => ({ label: product.name, value: product.id })),
    [legalProducts]
  );

  const validImportRows = useMemo(
    () => importRows.filter((row) => row.errors.length === 0 && row.values.nome.trim() !== ''),
    [importRows]
  );

  const skippedImportRows = useMemo(
    () => importRows.filter((row) => row.errors.length > 0 || row.values.nome.trim() === ''),
    [importRows]
  );

  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();

    const filtered = leads.filter((lead) => {
      if (!statusFilter && lead.status === 'archived') {
        return false;
      }

      if (statusFilter && lead.status !== statusFilter) {
        return false;
      }

      if (sourceFilter && lead.source !== sourceFilter) {
        return false;
      }

      if (legalProductFilter && lead.legalProductId !== legalProductFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [
        lead.name,
        lead.phone,
        lead.email,
        lead.source,
        lead.legalProductName,
        lead.caseDescription,
        lead.responsibleUser,
        lead.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
    return stableSort(filtered, (left, right) => {
      if (sort === 'name_asc') return compareText(left.name, right.name);
      if (sort === 'name_desc') return compareText(left.name, right.name, 'desc');
      if (sort === 'next_contact') return compareDate(left.nextContactAt, right.nextContactAt, 'asc') || compareText(left.name, right.name);
      if (sort === 'priority') return ({ urgent: 0, high: 1, normal: 2, low: 3 }[left.priority] ?? 9) - ({ urgent: 0, high: 1, normal: 2, low: 3 }[right.priority] ?? 9) || compareText(left.name, right.name);
      return compareDate(left.createdAt, right.createdAt, 'desc') || compareText(left.name, right.name);
    });
  }, [legalProductFilter, leads, search, sort, sourceFilter, statusFilter]);

  const selectedLead = useMemo(
    () => leads.find((lead) => lead.id === selectedLeadId) ?? null,
    [leads, selectedLeadId]
  );

  const selectedLeadHistory = useMemo(
    () => (selectedLead ? getSelectedLeadHistory(selectedLead) : []),
    [selectedLead]
  );

  const openNewLeadModal = () => {
    setSelectedLeadId('');
    setEditingLead(null);
    setNewLeadForm(emptyLeadForm(legalProducts[0]?.id ?? ''));
    setShowNewLeadModal(true);
  };

  const openEditLead = (lead: Lead) => {
    setSelectedLeadId('');
    setEditingLead(lead);
    setNewLeadForm(leadToFormState(lead, legalProducts[0]?.id ?? ''));
    setShowNewLeadModal(true);
  };

  const closeNewLeadModal = () => {
    if (isSavingLead) {
      return;
    }

    setShowNewLeadModal(false);
    setEditingLead(null);
    setNewLeadForm(emptyLeadForm(legalProducts[0]?.id ?? ''));
  };

  const handleSaveNewLead = async () => {
    if (isSavingLead) {
      return;
    }

    if (!newLeadForm.name.trim()) {
      addToast('Preencha o nome do lead.', 'warning');
      return;
    }

    setIsSavingLead(true);

    try {
      await createLead(buildCreateLeadPayload(newLeadForm));
      addToast('Lead salvo com sucesso.', 'success');
      setShowNewLeadModal(false);
      setNewLeadForm(emptyLeadForm(legalProducts[0]?.id ?? ''));
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o lead.', 'error');
    } finally {
      setIsSavingLead(false);
    }
  };

  const handleSaveLead = async () => {
    if (isSavingLead) {
      return;
    }

    if (!newLeadForm.name.trim()) {
      addToast('Preencha o nome do lead.', 'warning');
      return;
    }

    setIsSavingLead(true);

    try {
      const payload = buildCreateLeadPayload(newLeadForm);

      if (editingLead) {
        await updateLead(editingLead.id, payload);
        addToast('Lead atualizado com sucesso.', 'success');
      } else {
        await createLead(payload);
        addToast('Lead salvo com sucesso.', 'success');
      }

      setShowNewLeadModal(false);
      setEditingLead(null);
      setNewLeadForm(emptyLeadForm(legalProducts[0]?.id ?? ''));
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o lead.', 'error');
    } finally {
      setIsSavingLead(false);
    }
  };

  const refreshCurrentLeads = async () => {
    await loadLeads({
      status: statusFilter || undefined,
      legalProductId: legalProductFilter || undefined,
    });
  };

  const handleConvertLeadToClient = async (lead: Lead) => {
    if (
      lead.convertedClientId
    ) {
      addToast(
        'Este lead já está vinculado a um cliente.',
        'warning'
      );

      return;
    }

    try {
      const result =
        await convertLeadToClient(
          lead.id
        );

      await refreshCurrentLeads();

      addToast(
        result.reused
          ? 'Cliente já estava vinculado ao lead.'
          : 'Lead convertido em cliente com sucesso.',
        'success'
      );
    } catch (error) {
      if (
        error instanceof
          LeadConversionRequestError &&
        error.code ===
          'possible_client_duplicate' &&
        error.duplicates.length >
          0
      ) {
        const summary =
          formatClientDuplicateSummary(
            error.duplicates
          );

        const confirmed =
          window.confirm(
            `Este lead pode já estar cadastrado como cliente:\n\n${summary}\n\nDeseja criar uma nova ficha mesmo assim?`
          );

        if (
          !confirmed
        ) {
          addToast(
            'Conversão cancelada para evitar possível cliente duplicado.',
            'warning'
          );

          return;
        }

        try {
          const result =
            await convertLeadToClient(
              lead.id,
              {
                allowPossibleDuplicate:
                  true,
              }
            );

          await refreshCurrentLeads();

          addToast(
            result.reused
              ? 'Cliente já estava vinculado ao lead.'
              : 'Lead convertido em cliente com confirmação de possível duplicidade.',
            'success'
          );

          return;
        } catch (
          retryError
        ) {
          addToast(
            retryError instanceof Error
              ? retryError.message
              : 'Não foi possível concluir a conversão.',
            'error'
          );

          return;
        }
      }

      addToast(
        error instanceof Error
          ? error.message
          : 'Não foi possível converter o lead em cliente.',
        'error'
      );
    }
  };

  const handleCreateCaseFromLead = async (lead: Lead) => {
    if (!lead.convertedClientId) {
      addToast(
        'Converta o lead em cliente antes de criar o caso.',
        'warning'
      );

      return;
    }

    if (lead.convertedCaseId) {
      addToast(
        'Este lead já possui um caso criado.',
        'warning'
      );

      return;
    }

    try {
      const result =
        await createCaseFromLead(
          lead.id
        );

      await refreshCurrentLeads();

      addToast(
        result.reused
          ? 'Caso já estava vinculado ao lead.'
          : 'Caso criado a partir do lead com sucesso.',
        'success'
      );
    } catch (error) {
      addToast(
        error instanceof Error
          ? error.message
          : 'Não foi possível criar o caso.',
        'error'
      );
    }
  };

  const handleArchiveLead = async (lead: Lead) => {
    if (isSavingLead || deletingLeadId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar este lead? Esta ação removerá o lead da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingLeadId(lead.id);

    try {
      await deleteLead(lead.id);
      addToast('Lead arquivado com sucesso.', 'success');

      if (selectedLeadId === lead.id) {
        setSelectedLeadId('');
      }

      if (editingLead?.id === lead.id) {
        setShowNewLeadModal(false);
        setEditingLead(null);
        setNewLeadForm(emptyLeadForm(legalProducts[0]?.id ?? ''));
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar o lead.', 'error');
    } finally {
      setDeletingLeadId('');
    }
  };

  const handleMoveLeadToStatus = async (lead: Lead, nextStatus: string) => {
    const trimmedStatus = nextStatus.trim();

    if (!trimmedStatus || trimmedStatus === lead.status || isSavingLead || deletingLeadId) {
      return;
    }

    if (!leadStatusOptions.some((option) => option.value === trimmedStatus)) {
      addToast('Status inválido para movimentação.', 'warning');
      return;
    }

    try {
      await updateLead(lead.id, buildLeadPayloadFromLead(lead, trimmedStatus));
      addToast(`Lead movido para ${getStatusLabel(trimmedStatus)}.`, 'success');
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível mover o lead.', 'error');
      await loadLeads({
        status: statusFilter || undefined,
        legalProductId: legalProductFilter || undefined,
      });
    }
  };

  const handleLeadDragStart = (lead: Lead, event: any) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', lead.id);
    setDraggingLeadId(lead.id);
  };

  const handleLeadDragEnd = () => {
    setDraggingLeadId('');
    setDragOverStatus('');
  };

  const handleColumnDragOver = (status: string, event: any) => {
    event.preventDefault();
    if (dragOverStatus !== status) {
      setDragOverStatus(status);
    }
  };

  const handleColumnDrop = async (status: string, event: any) => {
    event.preventDefault();
    const leadId = event.dataTransfer.getData('text/plain');
    const lead = leads.find((item) => item.id === leadId);

    setDragOverStatus('');
    setDraggingLeadId('');

    if (!lead) {
      return;
    }

    await handleMoveLeadToStatus(lead, status);
  };

  const handleRetryLeads = () => {
    void loadLeads({
      status: statusFilter || undefined,
      legalProductId: legalProductFilter || undefined,
    });
  };

  const resetImportState = () => {
    setImportStep('upload');
    setImportFileName('');
    setImportRows([]);
    setImportErrors([]);
    setImportSummary(null);
    setIsImportingLeads(false);

    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
  };

  const openImportModal = () => {
    resetImportState();
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (isImportingLeads) {
      return;
    }

    setShowImportModal(false);
    resetImportState();
  };

  const processImportFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      addToast('Selecione um arquivo CSV válido.', 'warning');
      return;
    }

    try {
      const text = await file.text();
      const parsed = buildLeadImportRows(text);

      if (parsed.rows.length === 0) {
        addToast('O arquivo CSV não possui linhas de dados.', 'warning');
        return;
      }

      setImportFileName(file.name);
      setImportRows(parsed.rows);
      setImportErrors(parsed.rows.flatMap((row) => row.errors.map((error) => `Linha ${row.lineNumber}: ${error}`)));
      setImportSummary(null);
      setImportStep('preview');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Não foi possível ler o arquivo CSV.', 'error');
    }
  };

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    await processImportFile(file);
    event.target.value = '';
  };

  const handleImportFileDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];

    if (!file) {
      return;
    }

    await processImportFile(file);
  };

  const handleDownloadImportModel = () => {
    downloadTextFile(
      'modelo-importacao-leads-bm-legal-os.csv',
      buildLeadImportTemplateContent(),
      'text/csv;charset=utf-8'
    );
  };

  const handleConfirmImport = async () => {
    if (isImportingLeads) {
      return;
    }

    setIsImportingLeads(true);

    const summary: ImportSummary = {
      totalRead: importRows.length,
      imported: 0,
      failed: 0,
      ignored: skippedImportRows.length,
    };
    const validationErrors: string[] = [];

    try {
      for (const row of validImportRows) {
        const payload = buildLeadImportPayload(row, legalProducts);

        if (!payload) {
          summary.failed += 1;
          validationErrors.push(`Linha ${row.lineNumber}: Nome obrigatório.`);
          continue;
        }

        try {
          await createLead(payload);
          summary.imported += 1;
        } catch (error) {
          summary.failed += 1;
          const message = error instanceof Error ? error.message : 'Falha desconhecida ao importar lead.';
          validationErrors.push(`Linha ${row.lineNumber}: ${message}`);
        }
      }

      setImportErrors(validationErrors);
      setImportSummary(summary);
      setImportStep('result');
      addToast(
        `${summary.imported} lead(s) importado(s), ${summary.failed} falha(s) e ${summary.ignored} linha(s) ignorada(s).`,
        summary.failed > 0 ? 'warning' : 'success'
      );

      void loadLeads({
        status: statusFilter || undefined,
        legalProductId: legalProductFilter || undefined,
      });
    } finally {
      setIsImportingLeads(false);
    }
  };

  const saveButtonClassName = `px-3 py-1.5 rounded-lg text-xs font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap ${
    isSavingLead ? 'bg-primary-300' : 'bg-primary-500 hover:bg-primary-600'
  }`;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Leads / CRM</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              {isLoadingLeads ? 'Carregando leads...' : `${filteredLeads.length} leads · Pipeline comercial`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-background-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'table'
                    ? 'bg-background-50 text-foreground-800'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-table-line mr-1"></i>Tabela
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'kanban'
                    ? 'bg-background-50 text-foreground-800'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-layout-column-line mr-1"></i>Kanban
              </button>
            </div>
            <button
              onClick={openImportModal}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-line"></i> Importar Leads
            </button>
            <button
              onClick={openNewLeadModal}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> Novo Lead
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nome, telefone ou e-mail..."
            className="sm:w-72"
          />
          <FilterBar
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: leadStatusOptions.map((option) => ({ label: option.label, value: option.value })),
                value: statusFilter,
                onChange: setStatusFilter,
              },
              {
                key: 'source',
                label: 'Origem',
                options: leadSourceOptions.map((source) => ({ label: source, value: source })),
                value: sourceFilter,
                onChange: setSourceFilter,
              },
              {
                key: 'product',
                label: 'Produto',
                options: legalProductFilterOptions,
                value: legalProductFilter,
                onChange: setLegalProductFilter,
              },
            ]}
          />
          <SortSelect value={sort} onChange={setSort} options={[{ value: 'name_asc', label: 'A–Z' }, { value: 'name_desc', label: 'Z–A' }, { value: 'created_desc', label: 'Mais recentes' }, { value: 'next_contact', label: 'Próximo contato' }, { value: 'priority', label: 'Prioridade' }]} />
        </div>

        {isLoadingLeads && (
          <div className="rounded-lg border border-dashed border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-500">
            Carregando leads reais...
          </div>
        )}

        {leadsError && !isLoadingLeads && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{leadsError}</span>
            <button
              onClick={handleRetryLeads}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {viewMode === 'table' ? (
          <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      Nome
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                      Origem
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                      Produto
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                      Responsável
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                      Valor Est.
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">
                      Último Contato
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState
                          icon="ri-user-search-line"
                          title="Nenhum lead encontrado"
                          description="Ajuste os filtros ou cadastre um novo lead."
                          action={{
                            label: 'Novo Lead',
                            onClick: openNewLeadModal,
                            icon: 'ri-add-line',
                          }}
                        />
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedLeadId(lead.id)}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                              {lead.name}
                            </p>
                            <p className="text-xs text-foreground-400 whitespace-nowrap">
                              {lead.phone ?? '—'}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">
                            {getSourceLabel(lead.source)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">
                            {getProductLabel(lead)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={getStatusLabel(lead.status)} />
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">
                            {lead.responsibleUser ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right hidden lg:table-cell">
                          <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                            {formatCurrency(lead.estimatedValue)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <span className="text-sm text-foreground-500 whitespace-nowrap">
                            {formatDate(lead.lastContactAt)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditLead(lead);
                              }}
                              disabled={isSavingLead || Boolean(deletingLeadId)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Editar
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleArchiveLead(lead);
                              }}
                              disabled={isSavingLead || deletingLeadId === lead.id}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {deletingLeadId === lead.id ? 'Arquivando...' : 'Arquivar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex gap-3 min-w-max pb-2">
              {kanbanLeadStatusOptions.map((statusOption) => {
                const leadsInColumn = filteredLeads.filter((lead) => lead.status === statusOption.value);
                const isDragTarget = dragOverStatus === statusOption.value;

                return (
                  <div
                    key={statusOption.value}
                    className={`w-64 flex-shrink-0 rounded-xl transition-all ${
                      isDragTarget ? 'bg-primary-50/60 ring-2 ring-primary-200' : ''
                    }`}
                    onDragOver={(event) => handleColumnDragOver(statusOption.value, event)}
                    onDrop={(event) => void handleColumnDrop(statusOption.value, event)}
                  >
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                        {statusOption.label}
                      </span>
                      <span className="text-xs text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full">
                        {leadsInColumn.length}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {leadsInColumn.map((lead) => (
                        <div
                          key={lead.id}
                          onClick={() => setSelectedLeadId(lead.id)}
                          draggable
                          onDragStart={(event) => handleLeadDragStart(lead, event)}
                          onDragEnd={handleLeadDragEnd}
                          className={`bg-background-50 border border-background-200 rounded-lg p-3 cursor-pointer hover:border-primary-200 transition-all ${
                            draggingLeadId === lead.id ? 'opacity-60' : ''
                          }`}
                        >
                          <p className="text-sm font-medium text-foreground-800">{lead.name}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{getProductLabel(lead)}</p>
                          <div className="flex items-center justify-between mt-2">
                            <PriorityBadge priority={getPriorityLabel(lead.priority)} size="sm" />
                            <span className="text-xs font-semibold text-foreground-700">
                              {formatCurrency(lead.estimatedValue)}
                            </span>
                          </div>
                          <p className="text-[10px] text-foreground-400 mt-1.5">
                            {formatDate(lead.lastContactAt)}
                          </p>
                          <div className="mt-2">
                            <label className="block text-[10px] font-medium text-foreground-400 mb-1">
                              Mover para
                            </label>
                            <select
                              value={lead.status}
                              onChange={(event) => {
                                event.stopPropagation();
                                void handleMoveLeadToStatus(lead, event.target.value);
                              }}
                              onClick={(event) => event.stopPropagation()}
                              className="w-full px-2 py-1 rounded-md border border-background-200 bg-background-50 text-[11px] text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer"
                            >
                              {kanbanLeadStatusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditLead(lead);
                              }}
                              disabled={isSavingLead || Boolean(deletingLeadId)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Editar
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleArchiveLead(lead);
                              }}
                              disabled={isSavingLead || deletingLeadId === lead.id}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {deletingLeadId === lead.id ? 'Arquivando...' : 'Arquivar'}
                            </button>
                          </div>
                        </div>
                      ))}
                      {leadsInColumn.length === 0 && (
                        <div className="bg-background-100 border border-dashed border-background-200 rounded-lg p-4 text-center">
                          <p className="text-xs text-foreground-400">Vazio</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isLoadingLeads && !leadsError && filteredLeads.length === 0 && (
          <div className="rounded-xl border border-dashed border-background-200 bg-background-50 p-8 text-center">
            <p className="text-sm font-medium text-foreground-700">Nenhum lead encontrado</p>
            <p className="text-xs text-foreground-500 mt-1">Ajuste a busca ou cadastre um novo lead.</p>
          </div>
        )}

        <Drawer
          isOpen={!!selectedLead}
          onClose={() => setSelectedLeadId('')}
          title="Ficha do Lead"
          footer={
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  if (selectedLead) {
                    openEditLead(selectedLead);
                  }
                }}
                disabled={!selectedLead || isSavingLead || Boolean(deletingLeadId)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-edit-line mr-1"></i> Editar
              </button>
              <button
                onClick={() => {
                  if (selectedLead) {
                    void handleArchiveLead(selectedLead);
                  }
                }}
                disabled={!selectedLead || isSavingLead || deletingLeadId === selectedLead?.id}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingLeadId === selectedLead?.id ? 'Arquivando...' : 'Arquivar'}
              </button>
              <button
                onClick={() => {
                  if (selectedLead) {
                    void handleConvertLeadToClient(selectedLead);
                  }
                }}
                disabled={
                  !selectedLead ||
                  Boolean(selectedLead?.convertedClientId) ||
                  conversionLeadId === selectedLead?.id ||
                  isSavingLead ||
                  Boolean(deletingLeadId)
                }
                title={
                  selectedLead?.convertedClientId
                    ? 'Cliente já vinculado'
                    : 'Criar cliente reutilizando os dados do lead'
                }
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedLead?.convertedClientId
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
                }`}
              >
                <i
                  className={
                    selectedLead?.convertedClientId
                      ? 'ri-check-line mr-1'
                      : 'ri-user-add-line mr-1'
                  }
                ></i>

                {selectedLead?.convertedClientId
                  ? 'Cliente vinculado'
                  : conversionLeadId === selectedLead?.id &&
                      conversionAction === 'client'
                    ? 'Convertendo...'
                    : 'Converter em Cliente'}
              </button>

              <button
                onClick={() => {
                  if (selectedLead) {
                    void handleCreateCaseFromLead(selectedLead);
                  }
                }}
                disabled={
                  !selectedLead ||
                  !selectedLead?.convertedClientId ||
                  Boolean(selectedLead?.convertedCaseId) ||
                  conversionLeadId === selectedLead?.id ||
                  isSavingLead ||
                  Boolean(deletingLeadId)
                }
                title={
                  selectedLead?.convertedCaseId
                    ? 'Caso já criado'
                    : !selectedLead?.convertedClientId
                      ? 'Converta o lead em cliente primeiro'
                      : 'Criar caso reutilizando os dados do lead'
                }
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedLead?.convertedCaseId
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default'
                    : 'bg-accent-500 text-background-50 hover:bg-accent-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                <i
                  className={
                    selectedLead?.convertedCaseId
                      ? 'ri-check-line mr-1'
                      : 'ri-scales-line mr-1'
                  }
                ></i>

                {selectedLead?.convertedCaseId
                  ? 'Caso criado'
                  : conversionLeadId === selectedLead?.id &&
                      conversionAction === 'case'
                    ? 'Criando...'
                    : 'Criar Caso'}
              </button>
              <button
                disabled
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-400 cursor-not-allowed whitespace-nowrap opacity-60"
              >
                <i className="ri-file-text-line mr-1"></i> Gerar Proposta
              </button>
              <button
                disabled
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-400 cursor-not-allowed whitespace-nowrap opacity-60"
              >
                <i className="ri-folder-line mr-1"></i> Solicitar Documentos
              </button>
            </div>
          }
        >
          {selectedLead && (
            <div className="space-y-5">
              <div>
                <h4 className="text-base font-semibold text-foreground-900">{selectedLead.name}</h4>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Telefone</p>
                    <p className="text-sm text-foreground-700">{selectedLead.phone ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">E-mail</p>
                    <p className="text-sm text-foreground-700">{selectedLead.email ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Origem</p>
                    <p className="text-sm text-foreground-700">{getSourceLabel(selectedLead.source)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Produto</p>
                    <p className="text-sm text-foreground-700">{getProductLabel(selectedLead)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Status</p>
                    <p className="text-sm text-foreground-700">{getStatusLabel(selectedLead.status)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Prioridade</p>
                    <p className="text-sm text-foreground-700">{getPriorityLabel(selectedLead.priority)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Responsável</p>
                    <p className="text-sm text-foreground-700">{selectedLead.responsibleUser ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Valor Estimado</p>
                    <p className="text-sm font-semibold text-foreground-900">
                      {formatCurrency(selectedLead.estimatedValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Próximo Contato</p>
                    <p className="text-sm text-foreground-700">{formatDate(selectedLead.nextContactAt)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Último Contato</p>
                    <p className="text-sm text-foreground-700">{formatDate(selectedLead.lastContactAt)}</p>
                  </div>
                </div>
              </div>

              {(selectedLead.convertedClientId || selectedLead.convertedCaseId) && (
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className={`rounded-lg border p-2.5 ${
                      selectedLead.convertedClientId
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-background-200 bg-background-100'
                    }`}
                  >
                    <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                      Cliente
                    </p>

                    <p className="text-xs font-semibold text-foreground-700 mt-0.5">
                      {selectedLead.convertedClientId
                        ? 'Vinculado'
                        : 'Não convertido'}
                    </p>
                  </div>

                  <div
                    className={`rounded-lg border p-2.5 ${
                      selectedLead.convertedCaseId
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-background-200 bg-background-100'
                    }`}
                  >
                    <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                      Caso
                    </p>

                    <p className="text-xs font-semibold text-foreground-700 mt-0.5">
                      {selectedLead.convertedCaseId
                        ? 'Criado'
                        : 'Ainda não criado'}
                    </p>
                  </div>
                </div>
              )}

              <div className="p-3 bg-primary-50/50 border border-primary-100 rounded-lg">
                <p className="text-[10px] text-primary-500 uppercase tracking-wider font-semibold">
                  Descrição do Caso
                </p>
                <p className="text-sm text-primary-800 font-medium mt-0.5">
                  {selectedLead.caseDescription || 'Sem descrição informada.'}
                </p>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Observações
                </h5>
                <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">
                  {selectedLead.notes || 'Sem observações registradas.'}
                </p>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Histórico
                </h5>
                <div className="space-y-2">
                  {selectedLeadHistory.map((entry, index) => (
                    <div key={`${entry.date}-${index}`} className="flex gap-2 text-sm">
                      <span className="text-foreground-400 whitespace-nowrap">{entry.date}</span>
                      <span className="text-foreground-700">{entry.action}</span>
                      <span className="text-foreground-400">— {entry.user}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={showImportModal}
          onClose={closeImportModal}
          title="Importar Leads em CSV"
          width="w-[720px]"
          footer={
            importStep === 'preview' || importStep === 'result' ? (
              <div className="flex items-center justify-between w-full gap-3">
                <p className="text-xs text-amber-600 font-medium">
                  <i className="ri-information-line"></i> Revise os dados antes de importar. A importação criará
                  novos leads no sistema.
                </p>
                <div className="flex items-center gap-2">
                  {importStep === 'preview' ? (
                    <>
                      <button
                        onClick={resetImportState}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => void handleConfirmImport()}
                        disabled={isImportingLeads}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isImportingLeads ? 'Importando...' : 'Confirmar Importação'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setImportStep('upload');
                          setImportErrors([]);
                          setImportSummary(null);
                          setImportRows([]);
                          setImportFileName('');
                          if (importFileInputRef.current) {
                            importFileInputRef.current.value = '';
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Novo arquivo
                      </button>
                      <button
                        onClick={closeImportModal}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Fechar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : undefined
          }
        >
          <input
            ref={importFileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFileChange}
          />

          {importStep === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-background-300 rounded-xl p-8 text-center hover:border-primary-300 transition-colors cursor-pointer"
                onClick={() => importFileInputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => void handleImportFileDrop(event)}
              >
                <i className="ri-upload-cloud-2-line text-4xl text-foreground-300 mb-2"></i>
                <p className="text-sm font-medium text-foreground-600">
                  Arraste e solte o arquivo CSV aqui ou clique para selecionar
                </p>
                <p className="text-xs text-foreground-400 mt-1">
                  Nesta versão, a importação é apenas CSV. XLSX fica para uma sprint futura.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => importFileInputRef.current?.click()}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-upload-line mr-1"></i>Selecionar CSV
                </button>
                <button
                  type="button"
                  onClick={handleDownloadImportModel}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line mr-1"></i>Baixar modelo CSV
                </button>
              </div>
              <div className="p-3 bg-background-100 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-foreground-600">Colunas esperadas:</p>
                <p className="text-xs text-foreground-500 font-mono">
                  {leadImportColumns.map((column) => leadImportHeaderLabels[column]).join(', ')}
                </p>
                <p className="text-xs text-foreground-500">
                  Revise os dados antes de importar. A importação criará novos leads no sistema.
                </p>
              </div>
            </div>
          )}

          {importStep === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground-800">Pré-visualização</p>
                  <p className="text-xs text-foreground-500">
                    {importFileName} · {importRows.length} linha(s) lida(s)
                  </p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  {validImportRows.length} válidas
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Total lido</p>
                  <p className="text-base font-semibold text-foreground-900">{importRows.length}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Válidos</p>
                  <p className="text-base font-semibold text-foreground-900">{validImportRows.length}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Com erro / ignorados</p>
                  <p className="text-base font-semibold text-foreground-900">{skippedImportRows.length}</p>
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Erros de validação</p>
                  <ul className="space-y-1 text-xs text-amber-700 list-disc pl-4">
                    {importErrors.slice(0, 8).map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-background-100 rounded-lg p-3 max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Linha</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Nome</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Telefone</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Origem</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.slice(0, 8).map((row) => (
                      <tr key={row.lineNumber} className="border-b border-background-100">
                        <td className="py-1.5 px-2 text-foreground-500">{row.lineNumber}</td>
                        <td className="py-1.5 px-2 text-foreground-700">{row.values.nome || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.values.telefone || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.values.origem || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.errors.length > 0 ? 'Erro' : 'OK'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importStep === 'result' && importSummary && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground-800">Resultado da importação</p>
                  <p className="text-xs text-foreground-500">{importFileName}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  Concluído
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Lidas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.totalRead}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Importadas</p>
                  <p className="text-base font-semibold text-emerald-700">{importSummary.imported}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Falhas</p>
                  <p className="text-base font-semibold text-rose-700">{importSummary.failed}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Ignoradas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.ignored}</p>
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Erros encontrados</p>
                  <ul className="space-y-1 text-xs text-amber-700 list-disc pl-4">
                    {importErrors.slice(0, 10).map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={showNewLeadModal}
          onClose={closeNewLeadModal}
          title={editingLead ? 'Editar Lead' : 'Novo Lead'}
          width="w-[720px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeNewLeadModal}
                disabled={isSavingLead}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveLead}
                disabled={isSavingLead}
                className={saveButtonClassName}
              >
                {isSavingLead
                  ? 'Salvando...'
                  : editingLead
                    ? 'Salvar Alterações'
                    : 'Salvar Lead'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Nome *</label>
                <input
                  type="text"
                  value={newLeadForm.name}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, name: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Nome do lead"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Telefone</label>
                <input
                  type="text"
                  value={newLeadForm.phone}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, phone: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">E-mail</label>
                <input
                  type="email"
                  value={newLeadForm.email}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, email: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Origem</label>
                <select
                  value={newLeadForm.source}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, source: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {leadSourceOptions.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Produto Jurídico
                </label>
                <select
                  value={newLeadForm.legalProductId}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, legalProductId: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem produto jurídico</option>
                  {legalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={newLeadForm.status}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, status: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {leadStatusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Prioridade</label>
                <select
                  value={newLeadForm.priority}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({ ...previous, priority: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {leadPriorityOptions.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Responsável</label>
                <input
                  type="text"
                  value={newLeadForm.responsibleUser}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({
                      ...previous,
                      responsibleUser: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Nome do responsável"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Valor Estimado (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newLeadForm.estimatedValue}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({
                      ...previous,
                      estimatedValue: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="8500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Próximo Contato</label>
                <input
                  type="date"
                  value={newLeadForm.nextContactAt}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({
                      ...previous,
                      nextContactAt: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Último Contato</label>
                <input
                  type="date"
                  value={newLeadForm.lastContactAt}
                  onChange={(event) =>
                    setNewLeadForm((previous) => ({
                      ...previous,
                      lastContactAt: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição do Caso</label>
              <textarea
                value={newLeadForm.caseDescription}
                onChange={(event) =>
                  setNewLeadForm((previous) => ({
                    ...previous,
                    caseDescription: event.target.value,
                  }))
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Resumo da demanda do lead"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={newLeadForm.notes}
                onChange={(event) =>
                  setNewLeadForm((previous) => ({
                    ...previous,
                    notes: event.target.value,
                  }))
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Notas internas sobre o lead"
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default LeadsPage;
